// Item merges (IMPLEMENTATION §0.2 R11, R12). Two identical items below the family maximum become one item
// of the next tier (a NEW id) on the kept cell; units are conserved, so the ledger does not move.
// Quick Merge (u07): a double-tapped loose item merges its family from its tier upward, cascading.
// Merge All (u13, cooldown): every Merge-All family (not guns; deposits and buildings are not items),
// lowest tier first. Both pair by id and put each result on the lower-id cell, except that the tapped item
// (and whatever it merges into) always keeps its cell. The held (dragged) item is skipped. Neither undoes.
import type { RejectReason } from '../commands.ts';
import type { Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { type FamilyDef, isActive, type SimDefs } from '../defs.ts';
import { addItem, discover, famDef, findItem, removeItem } from '../entities.ts';
import type { MergeVia } from '../events.ts';
import type { FamId } from '../gen/ids.ts';
import type { FloorItem, ItemRef, SimState } from '../state.ts';
import { hasEffect } from '../unlocks.ts';

/** Removes a and b and creates their merge (tier + 1) at (x, y); emits `merge`. */
export function mergeItems(
  c: Ctx,
  a: FloorItem,
  b: FloorItem,
  x: Int,
  y: Int,
  via: MergeVia,
): FloorItem {
  removeItem(c, a.id);
  return mergeTaken(c, a, b, x, y, via);
}

/** Like mergeItems for a dragged item `a` already taken off the board (e.g. out of a machine slot). */
export function mergeTaken(
  c: Ctx,
  a: ItemRef,
  b: FloorItem,
  x: Int,
  y: Int,
  via: MergeVia,
): FloorItem {
  removeItem(c, b.id);
  const tier = a.tier + 1;
  const r = addItem(c, a.fam, tier, x, y);
  c.s.progress.merges += 1; // firstMerge (hand, Quick Merge, Merge All; machine merges never come here)
  const first = discover(c, a.fam, tier);
  c.ev.push({ e: 'merge', id: r.id, a: a.id, b: b.id, fam: a.fam, tier, x, y, first, via });
  return r;
}

function isDiscovered(s: SimState, fam: FamId, tier: Int): boolean {
  return s.progress.discovered.includes(`${fam}:${tier}`);
}

/** Families Merge All touches, in data order. */
export function mergeAllFamilies(d: SimDefs): FamilyDef[] {
  const out: FamilyDef[] = [];
  for (const f of d.families)
    if (f.mergeAll && isActive(d, f.version) && f.maxTier > 1) out.push(f);
  return out;
}

/** Why Quick Merge on `id` would do nothing, or null when it merges at least one pair. */
export function quickMergeBlock(s: SimState, d: SimDefs, id: Int): RejectReason | null {
  if (!hasEffect(s, d, 'quickMerge')) return 'notUnlocked';
  const it = findItem(s, id);
  if (it === null || s.transient.held === id) return 'stale';
  const f = famDef(d, it.fam);
  if (it.tier >= f.maxTier) return 'max';
  if (f.quickMerge === 'discovered' && !isDiscovered(s, it.fam, it.tier + 1)) return 'notUnlocked';
  const held = s.transient.held;
  for (const o of s.floor) {
    if (o.id !== id && o.id !== held && o.fam === it.fam && o.tier === it.tier) return null;
  }
  return 'needsTier';
}

/** Why Merge All would do nothing now, or null. */
export function mergeAllBlock(s: SimState, d: SimDefs): RejectReason | null {
  if (!hasEffect(s, d, 'mergeAll')) return 'notUnlocked';
  if (s.tick < s.progress.mergeAllReadyAt) return 'cooldown';
  const fams = mergeAllFamilies(d);
  const held = s.transient.held;
  // one count per family × tier (tiers ≤ 8 → stride 16)
  const seen: Int[] = [];
  for (const it of s.floor) {
    if (it.id === held) continue;
    for (let fi = 0; fi < fams.length; fi++) {
      const f = fams[fi] as FamilyDef;
      if (f.id !== it.fam || it.tier >= f.maxTier) continue;
      const k = fi * 16 + it.tier;
      const n = (seen[k] ?? 0) + 1;
      if (n >= 2) return null;
      seen[k] = n;
    }
  }
  return 'needsTier';
}

/**
 * Pairs and merges `fams` tier by tier from `fromTier` up (results cascade into the next tier). `focus` is
 * the tapped item id (0 = none): it pairs first and keeps its cell. Returns the number of merges.
 */
export function cascadeMerge(
  c: Ctx,
  fams: readonly FamilyDef[],
  fromTier: Int,
  focus: Int,
  via: MergeVia,
): Int {
  const held = c.s.transient.held;
  let top = 0;
  for (const f of fams) if (f.maxTier > top) top = f.maxTier;
  let n = 0;
  let fid = focus;
  for (let t = fromTier; t < top; t++) {
    for (const f of fams) {
      if (t >= f.maxTier) continue;
      if (f.quickMerge === 'discovered' && via === 'quick' && !isDiscovered(c.s, f.id, t + 1)) {
        continue;
      }
      const group: FloorItem[] = [];
      for (const it of c.s.floor) {
        if (it.fam === f.id && it.tier === t && it.id !== held) group.push(it);
      }
      for (let i = 1; i < group.length; i++) {
        if ((group[i] as FloorItem).id === fid) {
          const [x] = group.splice(i, 1);
          group.unshift(x as FloorItem);
          break;
        }
      }
      for (let i = 0; i + 1 < group.length; i += 2) {
        const keep = group[i] as FloorItem; // the focus, else the lower id (group is in id order)
        const other = group[i + 1] as FloorItem;
        const r = mergeItems(c, other, keep, keep.x, keep.y, via);
        if (keep.id === fid) fid = r.id;
        n++;
      }
    }
  }
  return n;
}
